/**
 * footprints.ts — PURE rules for the footprint decals (2026-09-10).
 *
 * "Add a footprint decal that applies to all grounds except when a versona
 * or the user is indoors, and make a specific decal for when it is snowing."
 *
 * A walker leaves a print every stride, alternating feet, each print set a
 * little to the side of the path and turned to the heading. The SURFACE
 * decides the decal: `ground` (a faint dark scuff that fades in seconds) on
 * every outdoor ground — terrain, roads, plateaus, caves — or `snow` (a
 * pressed, blue-shadowed hollow with a bright rim that lingers) when the
 * weather lays snow; `none` indoors, in the air, on a ride, under water or
 * in space. The module (FootprintDecalModule) only draws what these rules
 * return, so the harness pins the behaviour without a renderer.
 */

export type Surface = 'ground' | 'snow' | 'none';

/** Stride length at a walk (m) — a print per stride, alternating feet. */
export const STEP_LEN_M = 0.72;
/** Stride at a run (m); the stepper lengthens with speed between the two. */
export const STEP_LEN_RUN_M = 1.05;
/** Speed at which the stride is fully the run stride (m/s). */
export const RUN_SPEED_MPS = 5.5;
/** Each foot's lateral offset from the path (m). */
export const FOOT_OFFSET_M = 0.11;
/** Print size (m). */
export const PRINT_W_M = 0.14;
export const PRINT_L_M = 0.30;
/** How long a print stays (s). */
export const LIFE_GROUND_S = 24;
export const LIFE_SNOW_S = 80;
/** A print older than this fades (s before its end). */
export const FADE_TAIL_S = 5;
/** Snow cover above which prints press snow rather than scuff ground. */
export const SNOW_COVER_MIN = 0.35;
/** A stride longer than this is a teleport / snap, not a step — no prints across it (m). */
export const TELEPORT_M = 6;
/** Versonas leave prints inside this range of the player (m). */
export const WALKER_RANGE_M = 30;

export interface SurfaceInput {
  /** inside a dwelling / structure interior / sky-ship deck */
  indoors: boolean;
  /** station or ship deck interior */
  spaceInterior: boolean;
  /** riding a hovercraft, flying a ship, fly mode, space flight */
  riding: boolean;
  /** feet under water */
  submerged: boolean;
  /** feet this far above the ground (m) — airborne past a threshold */
  heightAboveGround: number;
  /** it is snowing right now */
  snowing: boolean;
  /** ground snow cover 0..1 */
  snowCover: number;
}

/** Which decal, if any, a step here leaves. */
export function surfaceFor(i: SurfaceInput): Surface {
  if (i.indoors || i.spaceInterior || i.riding || i.submerged) return 'none';
  if (i.heightAboveGround > 0.6) return 'none';
  if (i.snowing || i.snowCover >= SNOW_COVER_MIN) return 'snow';
  return 'ground';
}

/** Seconds a print of this surface stays. */
export function printLife(surface: Surface): number {
  return surface === 'snow' ? LIFE_SNOW_S : surface === 'ground' ? LIFE_GROUND_S : 0;
}

/** Opacity of a print aged `age` seconds with life `life`. */
export function printFade(age: number, life: number): number {
  if (age >= life) return 0;
  const left = life - age;
  return left >= FADE_TAIL_S ? 1 : left / FADE_TAIL_S;
}

/** Per-walker stepping state. */
export interface Stepper {
  lastX: number;
  lastZ: number;
  /** distance walked since the last print (m) */
  acc: number;
  /** which foot prints next: +1 right, −1 left */
  side: 1 | -1;
  /** seeded so the first print is not always the same foot */
  seeded: boolean;
}

export function newStepper(x: number, z: number, side: 1 | -1 = 1): Stepper {
  return { lastX: x, lastZ: z, acc: 0, side, seeded: true };
}

export interface Print { x: number; z: number; yaw: number; side: 1 | -1 }

/** Stride length for a ground speed (m/s): walk stride up to the run stride. */
export function strideFor(speed: number): number {
  const k = Math.max(0, Math.min(1, speed / RUN_SPEED_MPS));
  return STEP_LEN_M + (STEP_LEN_RUN_M - STEP_LEN_M) * k;
}

/**
 * Advance a walker to (x, z); returns the prints laid along the way (at most
 * a few per call — a long frame lays every stride it covered, never a
 * teleport's). `dt` gives the speed for the stride length.
 */
export function advance(st: Stepper, x: number, z: number, dt: number): Print[] {
  const dx = x - st.lastX, dz = z - st.lastZ;
  const d = Math.hypot(dx, dz);
  if (d <= 0) return [];
  if (d > TELEPORT_M) { st.lastX = x; st.lastZ = z; st.acc = 0; return []; }
  const speed = dt > 0 ? d / dt : 0;
  const stride = strideFor(speed);
  const yaw = Math.atan2(dx, dz);
  const ux = dx / d, uz = dz / d;
  // perpendicular to the heading (right-hand side)
  const rx = uz, rz = -ux;
  const out: Print[] = [];
  let walked = st.acc + d;
  let along = stride - st.acc; // distance from lastX along the path to the first print
  while (walked >= stride && out.length < 6) {
    const px = st.lastX + ux * along, pz = st.lastZ + uz * along;
    out.push({ x: px + rx * FOOT_OFFSET_M * st.side, z: pz + rz * FOOT_OFFSET_M * st.side, yaw, side: st.side });
    st.side = st.side === 1 ? -1 : 1;
    walked -= stride;
    along += stride;
  }
  st.acc = walked;
  st.lastX = x; st.lastZ = z;
  return out;
}
